import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { CodeEditor, Spinner } from 'axway-react-ui';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import cn from 'classnames';

const statuses = {
	// by default install command is applied
	inprogress: 'installing',
	completed: 'installed',
	restarting: 'restarting',
	failedrestart: 'failedrestart',
	cancelling: 'cancelling',
	cancelled: 'cancelled',
	success: 'success',
	failed: 'failed'
};

class OperationView extends React.Component {
	constructor(props) {
		super(props);
		if (props.operation === 'install') {
			statuses.inprogress = 'installing';
			statuses.completed = 'installed';
		} else {
			statuses.inprogress = 'uninstalling';
			statuses.completed = 'uninstalled';
		}

		IntlComponent.init(this);
		this.registerMessages();
		this.onShowLogClicked = this.onShowLogClicked.bind(this);
		this.state = {
			showLog: false
		};
	}

	onShowLogClicked(ev) {
		ev.stopPropagation();
		ev.preventDefault();
		this.setState({
			showLog: !this.state.showLog
		});
	}

	renderTitle() {
		const { status, operation } = this.props;
		if (status === statuses.success) {
			return (
				<div className="success">
					<span className="status-icon gg icon-GG-checkmark-circle" />
					{this.intl.formatMsg({ id: `${operation}_completeTitle` })}
				</div>
			);
		} else if (status === statuses.failed) {
			return (
				<div className="error">
					<span className="status-icon gg icon-GG-cross-circle" />
					{this.intl.formatMsg({ id: `${operation}_failedTitle` })}
				</div>
			);
		} else if (status === statuses.failedrestart) {
			return (
				<div className="error">
					<span className="status-icon gg icon-GG-cross-circle" />
					{this.intl.formatMsg({ id: `${operation}_completeWithErrorsTitle` })}
				</div>
			);
		} else if (status === statuses.cancelled) {
			return (
				<div className="error">
					<span className="status-icon gg icon-GG-cross-circle" />
					{this.intl.formatMsg({ id: `${operation}_cancelTitle` })}
				</div>
			);
		} else {
			return this.intl.formatMsg({ id: `${operation}_progressTitle` });
		}
	}

	renderBody() {
		const { componentName, status, log, operation } = this.props;
		const { showLog } = this.state;

		if (showLog) {
			return (
				<CodeEditor
					className="log"
					readOnly
					nocursor
					mode="simple"
					lineNumbers={false}
					lineWrapping
					copyButton
					code={log}
					tail
				/>
			);
		}

		if (status === statuses.cancelled) {
			return (
				<div>
					{this.intl.formatHTMLMsg(
						{ id: `${operation}_cancelledMessage` },
						{ name: <code>{componentName}</code> }
					)}
					<CodeEditor
						readOnly
						nocursor
						className="command-line"
						mode="shell"
						theme="eclipse"
						lineNumbers={false}
						lineWrapping
						copyButton
						code={`npm uninstall ${componentName}`}
					/>
				</div>
			);
		} else if (status === statuses.success) {
			return this.intl.formatHTMLMsg(
				{ id: `${operation}_successMessage` },
				{ name: <code>{componentName}</code> }
			);
		} else if (status === statuses.failed) {
			return (
				<div>
					{this.intl.formatHTMLMsg(
						{ id: `${operation}_failedMessage` },
						{ name: <code>{componentName}</code> }
					)}
					<CodeEditor
						readOnly
						nocursor
						className="command-line"
						mode="shell"
						theme="eclipse"
						lineNumbers={false}
						lineWrapping
						copyButton
						code={`npm ${operation} ${componentName}`}
					/>
				</div>
			);
		} else if (status === statuses.failedrestart) {
			return this.intl.formatHTMLMsg(
				{ id: `${operation}_failedRestartMessage` },
				{ name: <code>{componentName}</code> }
			);
		} else {
			// installing or restarting
			return this.intl.formatHTMLMsg(
				{ id: `${operation}_inProgressMessage` },
				{ name: <code>{componentName}</code> }
			);
		}
	}

	renderFooterAction() {
		const { status, operation } = this.props;
		const installLogText = this.state.showLog
			? this.intl.formatMsg({ id: `${operation}_hideLog` })
			: this.intl.formatMsg({ id: `${operation}_showLog` });

		if (status === statuses.restarting
			|| status === statuses.completed
			|| status === statuses.inprogress
			|| status === statuses.cancelling) {

			let spinnerMessage = this.intl.formatMsg({
				id: `${operation}_inProgressSpinner`
			});

			if (status === statuses.restarting) {
				spinnerMessage = this.intl.formatMsg({
					id: `${operation}_restartingSpinner`
				});
			}

			if (status === statuses.cancelling) {
				// Cancel is not available in uninstall use case
				spinnerMessage = this.intl.formatMsg({
					id: `${operation}_cancellingSpinner`
				});
			}

			const tooltip = <Tooltip id="tooltip">{installLogText}</Tooltip>;
			return (
				<OverlayTrigger placement="right" overlay={tooltip}>
					<div>
						<Spinner bsSize="small" />
						<span className="with-spinner">{spinnerMessage}</span>
					</div>
				</OverlayTrigger>
			);
		} else if (status === statuses.success
			|| status === statuses.failedrestart
			|| status === statuses.cancelled) {
			return installLogText;
		} else {
			// status === statuses.failed
			return this.state.showLog
				? this.intl.formatMsg({ id: `${operation}_hideErrorLog` })
				: this.intl.formatMsg({ id: `${operation}_showErrorLog` });
		}
	}

	renderButton() {
		const { status, onClose, onInstallCancel, onRetryRestart, configFile } = this.props;

		if (status === statuses.success
			&& configFile
			&& this.props.operation === 'install') {
			// Edit config is needed only after install
			return (
				<div>
					<Button
						bsStyle="primary"
						bsSize="large"
						className="okay"
						onClick={(ev) => onClose(ev, configFile)}
					>
						{this.intl.formatMsg(`Edit ${configFile}`)}
					</Button>
					<Button
						bsSize="large"
						bsStyle="link"
						className="cancel"
						onClick={onClose}
					>
						{this.intl.formatMsg('Close')}
					</Button>
				</div>
			);
		} else if (status === statuses.failed
			|| status === statuses.cancelled
			|| status === statuses.success) {
			return (
				<Button
					bsStyle="primary"
					bsSize="large"
					className="okay"
					onClick={onClose}
				>
					{this.intl.formatMsg('Ok')}
				</Button>
			);
		} else if (status === statuses.inprogress && this.props.operation === 'install') {
			// Cancel button is only available in Install mode
			return (
				<Button
					bsSize="large"
					bsStyle="link"
					className="cancel"
					onClick={onInstallCancel}
				>
					{this.intl.formatMsg('Cancel installation')}
				</Button>
			);
		} else if (status === statuses.failedrestart) {
			return (
				<Button
					bsSize="large"
					bsStyle="link"
					className="cancel"
					onClick={onRetryRestart}
				>
					{this.intl.formatMsg('Try again')}
				</Button>
			);
		}
	}

	render() {
		return (
			<Modal
				show
				backdrop="static"
				keyboard={false}
				className="install-results-view"
			>
				<Modal.Header>
					<Modal.Title>
						{this.renderTitle()}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className={cn({ large: this.state.showLog })}>
					{this.renderBody()}
				</Modal.Body>
				<Modal.Footer>
					<span className="modal-footer-action">
						<a
							onClick={this.onShowLogClicked}
							href="#"
						>
							{this.renderFooterAction()}
						</a>
					</span>
					{this.renderButton()}
				</Modal.Footer>
			</Modal>
		);
	}

	registerMessages() {
		// install messages
		this.intl.formatMsg('Hide install log', 'install_hideLog');
		this.intl.formatMsg('Show install log', 'install_showLog');
		this.intl.formatMsg('Hide error details', 'install_hideErrorLog');
		this.intl.formatMsg('Show error details', 'install_showErrorLog');
		this.intl.formatMsg('Installing...', 'install_inProgressSpinner');
		this.intl.formatMsg('Restarting server...', 'install_restartingSpinner');
		this.intl.formatMsg('Cancelling installation...', 'install_cancellingSpinner');
		this.intl.formatHTMLMsg('The installation of {name} was successful. However, there was a problem restarting the server. Check the console for more details, restart the server and click try again. The plugin may require additional configuration before it is available for use.', 'install_failedRestartMessage');
		this.intl.formatHTMLMsg('Installing {name}. This may take a few minutes.', 'install_inProgressMessage');
		this.intl.formatHTMLMsg('The installation of {name} failed.  Click "Show error details" to determine what went wrong.  You may need to close any editors or file explorers with your project open, or exit API Builder and attempt the installation manually using the following command:', 'install_failedMessage');
		this.intl.formatHTMLMsg('The installation of {name} succeeded.  However, the plugin may require additional configuration before it is available for use.', 'install_successMessage');
		this.intl.formatHTMLMsg('The installation of {name} was cancelled. In case of any side-effects you can exit API Builder and run the following command:', 'install_cancelledMessage');
		this.intl.formatMsg('Installation complete', 'install_completeTitle');
		this.intl.formatMsg('Installation failed', 'install_failedTitle');
		this.intl.formatMsg('Installation complete with errors', 'install_completeWithErrorsTitle');
		this.intl.formatMsg('Installation cancelled', 'install_cancelTitle');
		this.intl.formatMsg('Installation in progress...', 'install_progressTitle');

		// uninstall messages
		this.intl.formatMsg('Hide uninstall log', 'uninstall_hideLog');
		this.intl.formatMsg('Show uninstall log', 'uninstall_showLog');
		this.intl.formatMsg('Hide error details', 'uninstall_hideErrorLog');
		this.intl.formatMsg('Show error details', 'uninstall_showErrorLog');
		this.intl.formatMsg('Uninstalling...', 'uninstall_inProgressSpinner');
		this.intl.formatMsg('Restarting server...', 'uninstall_restartingSpinner');
		this.intl.formatMsg('Cancelling uninstall...', 'uninstall_cancellingSpinner');
		this.intl.formatHTMLMsg('The uninstall of {name} was successful. However, there was a problem restarting the server. Check the console for more details, restart the server and click try again.', 'uninstall_failedRestartMessage');
		this.intl.formatHTMLMsg('Uninstalling {name}. This may take a few minutes.', 'uninstall_inProgressMessage');
		this.intl.formatHTMLMsg('The uninstall of {name} failed.  Click "Show error details" to determine what went wrong.  You may need to close any editors or file explorers with your project open, or exit API Builder and attempt the uninstall manually using the following command:', 'uninstall_failedMessage');
		this.intl.formatHTMLMsg('Successfully uninstalled {name}.', 'uninstall_successMessage');
		this.intl.formatMsg('Uninstall complete', 'uninstall_completeTitle');
		this.intl.formatMsg('Uninstall failed', 'uninstall_failedTitle');
		this.intl.formatMsg('Uninstall complete with errors', 'uninstall_completeWithErrorsTitle');
		this.intl.formatMsg('Uninstall in progress...', 'uninstall_progressTitle');
	}
}

OperationView.propTypes = {
	/**
	 * The name of the component being installed.
	 */
	componentName: PropTypes.string.isRequired,
	/**
	 *
	 */
	status: PropTypes.oneOf(Object.values(statuses).concat([ 'uninstalling', 'uninstalled' ])),
	/**
	 * The install log.
	 */
	log: PropTypes.string.isRequired,
	/**
	 * The optional name of the config file
	 * which relates to the installed plugin.
	 */
	configFile: PropTypes.string,
	/**
	 * A callback to dismiss the dialog when install is over
	 */
	onClose: PropTypes.func.isRequired,
	/*
	 * A callback to cancel the install.
	 * This is optional for uninstall.
	 */
	onInstallCancel: PropTypes.func,
	/*
	 * A callback to trigger a restart await manually.
	 * Used when the server failed and the user has to get
	 * it running again before continuing
	 */
	onRetryRestart: PropTypes.func.isRequired
};

export default injectIntl(OperationView);
